import Typography from '@material-ui/core/Typography'
import cloneDeep from 'lodash/cloneDeep'
import { MouseEvent, useEffect, useMemo, useState } from 'react'

import { toSafeUrl } from '@karakuri-web/common/utils'

import { uniqueKey } from '../../utils'

import { AnswerActions } from './AnswerActions'
import { buildAnswerPath } from './buildAnswerLink'
import { AnswerCarouselProps, AnswerCarouselSelectionState } from './interface'
import { renderCarousel } from './renderCarousel'

export function getPageIndex(carouselId: string, state: AnswerCarouselSelectionState): number {
  const carouselData = state[carouselId]
  if (carouselData) {
    const currentPageId = carouselData.currentPageID
    const pageIds = Object.keys(carouselData.selectionPath)
    const pageIndex = pageIds.indexOf(currentPageId)
    return pageIndex !== -1 ? pageIndex : 0
  } else {
    return 0
  }
}

const PAGE_CHANGE_TIMEOUT = 300
let pageChangeTimer: number

export function AnswerCarousel({
  index,
  baseClassName,
  carousel,
  skillApi,
  cardId,
  carouselSelectionState,
  setCarouselSelectionState,
  selectionPath,
  updateSelectionPath,
  operatorEscalationKeyword,
  karakuriWebRootUrl,
  onClick,
  pathPrefix,
  session,
}: AnswerCarouselProps) {
  const carouselID = carousel.id
  const [pageID, setPageID] = useState<string>(carousel.contents[index].id)
  const defaultCarouselSelectionPath = useMemo(() => {
    const defaultCarouselSelectionPath = {
      currentPageID: carousel.contents[0].id,
      selectionPath: carousel.contents.reduce(
        (acc, item) => {
          acc[item.id] = []
          return acc
        },
        {} as Record<string, string[]>
      ),
    }

    return defaultCarouselSelectionPath
  }, [carousel])

  const onChange = (now?: number, _prev?: number) => {
    if (!carousel.contents.length) return
    if (now === undefined) return
    if (!carousel.contents[now].id) return

    const pageId = carousel.contents[now].id

    if (pageChangeTimer) {
      clearTimeout(pageChangeTimer)
    }
    pageChangeTimer = window.setTimeout(() => {
      updatePageID(pageId)
    }, PAGE_CHANGE_TIMEOUT)
  }

  const updateCarouselSelectionState = (state: AnswerCarouselSelectionState) => {
    setCarouselSelectionState(state)
    updateSelectionPath({ carousel: state })
  }

  const ensureCarouselSelectionState = (): AnswerCarouselSelectionState => {
    const cloneCarouselSelectionState = cloneDeep(carouselSelectionState)
    if (!cloneCarouselSelectionState[carouselID]) {
      cloneCarouselSelectionState[carouselID] = defaultCarouselSelectionPath
    }
    return cloneCarouselSelectionState
  }

  const updatePageID = (pageId: string) => {
    const cloneCarouselSelectionState = ensureCarouselSelectionState()
    cloneCarouselSelectionState[carouselID].currentPageID = pageId
    setPageID(pageId)
    updateCarouselSelectionState(cloneCarouselSelectionState)
  }

  const updateCarouselSelectionPath = (...paths: string[]) => {
    const cloneCarouselSelectionState = ensureCarouselSelectionState()
    cloneCarouselSelectionState[carouselID].selectionPath[pageID] = paths
    updateCarouselSelectionState(cloneCarouselSelectionState)
  }

  const onActionSelected = (
    e: MouseEvent<any>,
    id: string,
    external: boolean,
    uri?: string | null
  ) => {
    e.preventDefault()
    if (external) {
      // 会話カードの回答は一般ユーザーも編集できるため、javascript: 等のURLは開かない（AnswerActions のリンクと同じ判定）
      const safeUri = toSafeUrl(uri)
      if (safeUri) {
        return window.open(safeUri, '_blank', 'noopener,status=yes,scrollbars=yes,resizable=yes')
      }
      onClick(buildAnswerPath(id))
    } else {
      updateCarouselSelectionPath(id)
    }
  }

  useEffect(() => {
    onChange(index)
    return () => {
      clearTimeout(pageChangeTimer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Typography paragraph component="div" className={baseClassName}>
      {renderCarousel(index, carousel, skillApi, cardId, onChange)}
      {carousel.actions?.length && (
        <AnswerActions
          key={uniqueKey('answer-actions', cardId, carouselID, pageID)}
          className="faq-answer-content__button"
          cardId={cardId}
          actions={carousel.actions.filter(a => a.key === pageID)[0]?.value}
          pathPrefix={pathPrefix}
          selectionPath={selectionPath}
          operatorEscalationKeyword={operatorEscalationKeyword}
          karakuriWebRootUrl={karakuriWebRootUrl}
          onActionSelected={onActionSelected}
          session={session}
        />
      )}
    </Typography>
  )
}
